type TablePaginationProps = {
  totalItems: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
};

const TablePagination = ({
  totalItems,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [7, 25, 50],
}: TablePaginationProps) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const start = totalItems === 0 ? 0 : ((safePage - 1) * pageSize) + 1;
  const end = Math.min(totalItems, safePage * pageSize);

  const pageButtons = Array.from({ length: totalPages }, (_, index) => index + 1).filter((page) => {
    if (totalPages <= 7) return true;
    if (page === 1 || page === totalPages) return true;
    return Math.abs(page - safePage) <= 1;
  });

  return (
    <div className="flex flex-col gap-3 border-t border-slate-200 px-3 py-3 text-sm text-slate-600 sm:px-4 md:flex-row md:items-center md:justify-between">
      <div>
        Showing {start}-{end} of {totalItems}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2">
          <span className="text-slate-500">Rows</span>
          <select
            className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </label>

        <button
          type="button"
          className="rounded border border-slate-300 px-2 py-1 disabled:opacity-40"
          onClick={() => onPageChange(safePage - 1)}
          disabled={safePage <= 1}
        >
          Prev
        </button>

        {pageButtons.map((page, index) => {
          const previous = pageButtons[index - 1];
          const showGap = previous !== undefined && page - previous > 1;
          return (
            <span key={page} className="flex items-center gap-2">
              {showGap && <span className="px-1 text-slate-400">...</span>}
              <button
                type="button"
                className={`rounded px-2 py-1 ${page === safePage ? 'bg-[#1F8F5F] text-white' : 'border border-slate-300'}`}
                onClick={() => onPageChange(page)}
              >
                {page}
              </button>
            </span>
          );
        })}

        <button
          type="button"
          className="rounded border border-slate-300 px-2 py-1 disabled:opacity-40"
          onClick={() => onPageChange(safePage + 1)}
          disabled={safePage >= totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default TablePagination;

